import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Add } from '@mint-lib/icons';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@mint-lib/tw-components';

import { GlossaryType, type GlossaryListQueryResult } from '../../api/types.js';
import { useIsReadOnlyUser } from '../../hooks/useIsReadOnlyUser.js';
import { Tooltip } from '../atoms/Tooltip.jsx';
import { AddGlossariesItemDialog } from './AddGlossariesItemDialog.jsx';

type AddGlossaryItemButtonProps = {
  isPending: boolean;
  platformsGlossaryData: GlossaryListQueryResult;
  channelsGlossaryData: GlossaryListQueryResult;
  isPlatformAddingDisabled?: boolean;
  isChannelsAddingDisabled?: boolean;
  className?: string;
};

export const AddGlossaryItemButton = ({
  isPending,
  platformsGlossaryData,
  channelsGlossaryData,
  isPlatformAddingDisabled,
  isChannelsAddingDisabled,
  className,
}: AddGlossaryItemButtonProps) => {
  const { t } = useTranslation('@myn/import-hub');
  const isReadOnlyUser = useIsReadOnlyUser();

  const [optionsDialogToOpen, setOptionsDialogToOpen] =
    useState<GlossaryType | null>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="contained"
            color="primary"
            disabled={isReadOnlyUser}
            className={className}
            data-dd-action-name="Add item"
          >
            <Add className="size-4" />
            {t('Add item')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="z-dropdown w-(--radix-dropdown-menu-trigger-width)"
        >
          <Tooltip
            asChild
            description={t(
              'All currently integrated channels have been added.',
            )}
            side="left"
            disabled={!isChannelsAddingDisabled}
          >
            <div>
              <DropdownMenuItem
                data-dd-action-name="Channel"
                disabled={isChannelsAddingDisabled}
                onClick={() => {
                  setOptionsDialogToOpen('channel');
                  setIsMenuOpen(false);
                }}
              >
                {t('Channel')}
              </DropdownMenuItem>
            </div>
          </Tooltip>
          <Tooltip
            asChild
            description={t(
              'All currently integrated platforms have been added.',
            )}
            side="left"
            disabled={!isPlatformAddingDisabled}
          >
            <div>
              <DropdownMenuItem
                data-dd-action-name="Platform"
                disabled={isPlatformAddingDisabled}
                onClick={() => {
                  setOptionsDialogToOpen('platform');
                  setIsMenuOpen(false);
                }}
              >
                {t('Platform')}
              </DropdownMenuItem>
            </div>
          </Tooltip>
        </DropdownMenuContent>
      </DropdownMenu>
      {optionsDialogToOpen && (
        <AddGlossariesItemDialog
          isLoading={isPending}
          glossaryType={optionsDialogToOpen}
          glossaryItems={
            optionsDialogToOpen === 'platform'
              ? platformsGlossaryData.items
              : channelsGlossaryData.items
          }
          onClose={() => setOptionsDialogToOpen(null)}
        />
      )}
    </>
  );
};
